import type { Discount, DiscountAudience, DiscountScope } from '@erp/shared';
import { Ban, BadgePercent, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

type StatusFilter = '' | 'active' | 'inactive';
/** '' = every scope. The empty string is the sentinel because 'all' is a real scope. */
type ScopeFilter = '' | DiscountScope;
/** Same sentinel trick on the customer axis — 'all' there means "every customer". */
type AudienceFilter = '' | DiscountAudience;

export function DiscountsPage(): JSX.Element {
  const { t, i18n } = useTranslation('discounts');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const canWrite = usePermission('discounts.write');
  const [discounts, setDiscounts] = useState<Discount[] | null>(null);
  const [status, setStatus] = useState<StatusFilter>('');
  const [scope, setScope] = useState<ScopeFilter>('');
  const [audience, setAudience] = useState<AudienceFilter>('');

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Discount[] }>('/discounts');
    setDiscounts(r.data.items);
  };

  useEffect(() => {
    void reload();
  }, []);

  const visible = useMemo(
    () => (discounts ?? []).filter((d) => {
      if (status === 'active' && !d.isActive) return false;
      if (status === 'inactive' && d.isActive) return false;
      if (scope && d.scope !== scope) return false;
      if (audience && d.audience !== audience) return false;
      return true;
    }),
    [discounts, status, scope, audience],
  );

  if (!discounts) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onDeactivate = async (d: Discount): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { name: d.name }), tone: 'danger' }))) return;
    try {
      await api().patch(`/discounts/${d.id}`, { isActive: false });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (d: Discount): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmReactivate', { name: d.name }) }))) return;
    try {
      await api().patch(`/discounts/${d.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onDelete = async (d: Discount): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDelete', { name: d.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/discounts/${d.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const fmtDate = (s: string | null): string => (s ? formatDate(s, { locale: i18n.language }) : '…');

  const valueText = (d: Discount): string =>
    d.discountType === 'percent'
      ? `${Number(d.discountValue)}%`
      : d.currencyCode
        ? formatMoney(d.discountValue, { locale: i18n.language, currency: d.currencyCode })
        : d.discountValue;

  /** A rule whose product/brand/category was deleted still exists — say so rather than showing a blank. */
  const targetText = (d: Discount): string =>
    d.scope === 'all' ? t('scope.all') : d.targetName ?? t('list.missingTarget');

  /**
   * The rule's REACH in one line, so the owner can see who a discount is for
   * without opening the row. A targeted rule is named by its customer types
   * rather than by the mode — "ماركيت" says more than "chosen customer types".
   * The names arrive positionally aligned with the ids, so a blank one is a list
   * that has since been deleted and must be flagged, not hidden.
   */
  const audienceText = (d: Discount): string =>
    (d.audience === 'price_list'
      ? d.audiencePriceListNames.map((n) => n || t('list.missingCustomerType')).join(', ')
      : t(`audience.${d.audience}`));

  const columns: DataTableColumn<Discount>[] = [
    {
      id: 'name',
      header: t('list.columns.name'),
      sortValue: (d) => d.name,
      searchValue: (d) => d.name,
      cellClassName: 'font-medium',
      cell: (d) =>
        canWrite ? (
          <Link
            to={`/sales/discounts/${d.id}/edit`}
            className="text-fg underline-offset-4 hover:text-brand hover:underline"
          >
            {d.name}
          </Link>
        ) : (
          d.name
        ),
    },
    {
      id: 'target',
      header: t('list.columns.target'),
      sortValue: (d) => `${d.scope} ${d.targetName ?? ''}`,
      searchValue: (d) => d.targetName,
      cell: (d) => (
        <div className="flex items-center gap-2">
          <Badge variant={d.scope === 'all' ? 'info' : 'secondary'}>{t(`scope.${d.scope}`)}</Badge>
          {d.scope !== 'all' && (
            <span className={d.targetName ? undefined : 'text-danger'}>{targetText(d)}</span>
          )}
        </div>
      ),
    },
    {
      id: 'value',
      header: t('list.columns.value'),
      align: 'end',
      sortValue: (d) => Number(d.discountValue),
      cellClassName: 'font-mono tabular-nums',
      cell: valueText,
    },
    {
      id: 'appliesTo',
      header: t('list.columns.appliesTo'),
      sortValue: (d) => d.appliesTo,
      cell: (d) => <Badge variant="outline">{t(`appliesTo.${d.appliesTo}`)}</Badge>,
    },
    {
      id: 'audience',
      header: t('list.columns.audience'),
      sortValue: audienceText,
      searchValue: audienceText,
      cell: (d) => {
        if (d.audience !== 'price_list') {
          // 'all' is the default reach, so it stays quiet; "normal customers" is a
          // deliberate narrowing and reads as one.
          return (
            <Badge variant={d.audience === 'all' ? 'outline' : 'info'}>{t(`audience.${d.audience}`)}</Badge>
          );
        }
        // Fail-closed on the server too: a targeted rule naming nothing matches
        // nobody, which is worth shouting about rather than rendering as blank.
        if (d.audiencePriceListIds.length === 0) {
          return <Badge variant="danger">{t('list.audienceNobody')}</Badge>;
        }
        return (
          <div className="flex flex-wrap items-center gap-1">
            {d.audiencePriceListIds.map((listId, i) => (
              <Badge key={listId} variant={d.audiencePriceListNames[i] ? 'info' : 'danger'}>
                {d.audiencePriceListNames[i] || t('list.missingCustomerType')}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      id: 'window',
      header: t('list.columns.window'),
      sortValue: (d) => d.endsAt ?? '',
      cell: (d) =>
        d.startsAt || d.endsAt ? (
          <span className="text-xs text-fg-muted">
            {fmtDate(d.startsAt)} → {fmtDate(d.endsAt)}
          </span>
        ) : (
          <span className="text-xs text-fg-subtle">{t('list.noWindow')}</span>
        ),
    },
    {
      id: 'status',
      header: t('list.columns.status'),
      sortValue: (d) => (d.isActive ? 1 : 0),
      cell: (d) => (
        <Badge variant={d.isActive ? 'success' : 'secondary'}>
          {t(`status.${d.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-36',
            cell: (d: Discount) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/sales/discounts/${d.id}/edit`)}
                  aria-label={`${t('actions.edit')} ${d.name}`}
                  title={t('actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {d.isActive ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onDeactivate(d)}
                    aria-label={`${t('actions.deactivate')} ${d.name}`}
                    title={t('actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onReactivate(d)}
                    aria-label={`${t('actions.reactivate')} ${d.name}`}
                    title={t('actions.reactivate')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void onDelete(d)}
                  aria-label={`${t('actions.delete')} ${d.name}`}
                  title={t('actions.delete')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t('description')}</p>
        </div>
        {canWrite && <Button onClick={() => navigate('/sales/discounts/new')}>{t('create')}</Button>}
      </header>

      {discounts.length === 0 ? (
        <EmptyState
          icon={<BadgePercent className="h-8 w-8" aria-hidden />}
          title={t('list.empty')}
          description={t('list.emptyDescription')}
          action={canWrite ? <Button onClick={() => navigate('/sales/discounts/new')}>{t('create')}</Button> : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={visible}
            rowKey={(d) => d.id}
            searchPlaceholder={t('searchPlaceholder')}
            rowClassName={(d) => (d.isActive ? undefined : 'opacity-60')}
            toolbar={
              <div className="flex flex-wrap items-center gap-1.5">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as StatusFilter)}
                  aria-label={t('list.columns.status')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted"
                >
                  <option value="">{t('list.filters.allStatuses')}</option>
                  <option value="active">{t('status.active')}</option>
                  <option value="inactive">{t('status.inactive')}</option>
                </select>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value as ScopeFilter)}
                  aria-label={t('list.columns.target')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted"
                >
                  <option value="">{t('list.filters.allScopes')}</option>
                  <option value="product">{t('scope.product')}</option>
                  <option value="brand">{t('scope.brand')}</option>
                  <option value="category">{t('scope.category')}</option>
                  <option value="all">{t('scope.all')}</option>
                </select>
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as AudienceFilter)}
                  aria-label={t('list.columns.audience')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted"
                >
                  <option value="">{t('list.filters.allAudiences')}</option>
                  <option value="all">{t('audience.all')}</option>
                  <option value="price_list">{t('audience.price_list')}</option>
                  <option value="no_list">{t('audience.no_list')}</option>
                </select>
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
}
